/* eslint-disable complexity */
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import ShareIcon from '@mui/icons-material/Share';
import {LoadingButton} from '@mui/lab';
import {
  Alert,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Typography,
} from '@mui/material';
import update, {Spec} from 'immutability-helper';
import _ from 'lodash';
import {useSnackbar} from 'notistack';
import {useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

import API, {getMessagesFromApiError} from '../../api/axios';
import {apiBaseUrl} from '../../api/urls';
import {useAppDispatch, useAppSelector} from '../../hooks/redux';
import {
  Dashboard,
  DashboardDateRange,
  DashboardDefault,
  DashboardDrilldownAction,
  DashboardEntity,
  DashboardFilter,
  DashboardUpsertInputBody,
} from '../../interfaces/Dashboard';
import reduxActions from '../../redux/actions';
import {Role} from '../../utils/acl';
import {checkPrivilegeAction} from '../../utils/dashboard';
import {resolveDateRangePreset} from '../../utils/dateRange';
import {
  dashboardHasDatePanels,
  PanelCode,
  panelUsesDashboardDate,
} from '../../utils/panels';
import AccessControl from '../common/AccessControl';
import {CloseSnackbarAction} from '../common/CloseSnackbarButton';
import ModalFixed from '../common/ModalFixed';
import SnackbarMessages from '../common/SnackbarMessages';
import {DashboardHistoryReportType} from '../dashboard-panels/DashboardPanelItem';
import {DashboardPanelSelectButton} from '../dashboard-panels/DashboardPanelSelectButton';
import {DashboardProvider} from './DashboardContext';
import {DashboardDateRangeControl} from './DashboardDateRangeControl';
import DashboardDebugPanel from './DashboardDebugPanel';
import {DashboardHeaderActionsSlot} from './DashboardHeaderActionsSlot';
import DashboardItemShareModal from './DashboardItemShareModal';
import DashboardEntityList from './entities/DashboardEntityList';

interface Props {
  pk: number;
  primary: boolean;
  item?: Dashboard;
  prefetch?: boolean;
  onSubmitted?: (item: Dashboard) => void;
}

const getDashboardInput = (
  item?: Dashboard | DashboardDefault
): DashboardUpsertInputBody => {
  return {
    name: item?.name ?? null,
    data: {
      entities:
        item?.data.entities?.map((i) => ({
          ...i,
          id: i.id ?? Math.random().toString(16).substring(2, 14),
        })) ?? [],
      // Preserve the dashboard-wide date range so the chip shows the active
      // preset after reload and the range is included when the owner saves.
      dateRange: item?.data?.dateRange,
    },
  };
};

const getDashboardFilter = (
  input?: DashboardUpsertInputBody | DashboardDefault
): DashboardFilter => {
  const filter: DashboardFilter = {};

  input?.data?.entities?.forEach((el) => {
    filter[el.id] = {
      chartType: el?.panel?.data?.chartType ?? 'bar',
      params: el?.panel?.data?.params ?? {},
      refreshInterval: el?.panel?.data?.refreshInterval ?? null,
      columnWidths: el?.panel?.data?.columnWidths ?? {},
      columnOrder: el?.panel?.data?.columnOrder,
      shownFields: el?.panel?.data?.shownFields,
      autoReload: el?.panel?.data?.autoReload,
    };
  });

  return filter;
};

/**
 * Fans a resolved date range out to every participating panel in the filter
 * map. Panels that are detached from the dashboard date are skipped unless
 * applyToOverridden is true.
 */
const applyDashboardDateToFilter = (
  resolved: {date_start: string; date_end: string},
  entities: DashboardEntity[],
  currentFilter: DashboardFilter,
  applyToOverridden: boolean
): DashboardFilter => {
  const next = {...currentFilter};

  for (const entity of entities) {
    if (entity.type !== 'panel' || !entity.panel?.code) {
      continue;
    }
    if (!panelUsesDashboardDate(entity.panel.code)) {
      continue;
    }

    const isDetached = !!entity.panel?.data?.detachedFromDashboardDate;
    if (isDetached && !applyToOverridden) {
      continue;
    }

    const existing = next[entity.id] ?? {};
    next[entity.id] = {
      ...existing,
      params: {
        ...existing.params,
        date_start: resolved.date_start,
        date_end: resolved.date_end,
      },
    };
  }

  return next;
};

const DashboardItem = ({
  pk,
  primary,
  item,
  prefetch = true,
  onSubmitted,
}: Props) => {
  const {t} = useTranslation();
  const me = useAppSelector(({app}) => app.me);
  const reduxDispatch = useAppDispatch();
  const [action, setAction] = useState<boolean>(false);

  /*********/
  /* fetch */
  /*********/

  const [fetchedData, setFetchedData] = useState<Dashboard | undefined>(
    _.cloneDeep(item)
  );

  const [fetchedErrors, setFetchedErrors] = useState<string[]>([]);
  const [fetchedInProgress, setFetchedInProgress] = useState(false);

  const visibilityKeys = [
    'visible_to_client_viewer',
    'visible_to_client_manager',
    'visible_to_strata_manager',
    'visible_to_super_user',
  ] as const;
  const hasVisibleItems = useMemo(
    () =>
      fetchedData
        ? visibilityKeys.map((key) => fetchedData[key]).some(Boolean) // Check if any are true
        : false,
    [fetchedData]
  );

  const fetchData = async () => {
    setFetchedInProgress(true);
    let respDefaultDash = null;
    try {
      respDefaultDash = await API.get<DashboardDefault>(
        `${apiBaseUrl}/dashboard/configuration?dashboard_id=${pk}`
      );
    } catch (error: any) {
      const messages = getMessagesFromApiError(error, 'STRA_6705bc41');
      setFetchedErrors(messages);
    }

    const resp = await API.get<Dashboard>(
      `${apiBaseUrl}/dashboard/${primary ? 'primary' : pk}`
    );
    if (!_.isEqual(item, resp.data)) {
      setFilter(getDashboardFilter(resp.data));
      setInput(getDashboardInput(resp.data));
      updateRoles(resp.data);
      setFetchedData(resp.data);
    }
    savedEntitiesRef.current = resp.data?.data?.entities ?? [];
    /**
     * Applies a stored dashboard date range (if any) onto an initial filter.
     * `entitiesSource` provides the entity list (for detach-flag checks).
     * `rangeSource` provides the dateRange — defaults to `entitiesSource`.
     * Non-owners use the dashboard row as the range source so they inherit the
     * owner's range even though their entity list comes from their config blob.
     */
    const applyStoredRange = (
      initialFilter: DashboardFilter,
      entitiesSource: DashboardDefault | Dashboard | null | undefined,
      rangeSource?: DashboardDefault | Dashboard | null
    ): DashboardFilter => {
      const dateRange = ((rangeSource ?? entitiesSource) as any)?.data
        ?.dateRange;
      if (!dateRange) {
        return initialFilter;
      }
      const resolved = resolveDateRangePreset(
        dateRange.preset,
        dateRange.date_start,
        dateRange.date_end
      );
      const entities: DashboardEntity[] =
        (entitiesSource as any)?.data?.entities ?? [];
      // On load, never force-apply to detached panels (applyToOverridden=false)
      return applyDashboardDateToFilter(
        resolved,
        entities,
        initialFilter,
        false
      );
    };

    if (
      checkPrivilegeAction(me, resp.data, 'EDIT') &&
      !hasVisibleItems &&
      !resp.data.product
    ) {
      const initialFilter = getDashboardFilter(resp.data);
      setFilter(applyStoredRange(initialFilter, resp.data));
      setInput(getDashboardInput(resp.data));
      updateRoles(resp.data);
      setFetchedData(resp.data);
    } else if (respDefaultDash && !_.isEmpty(respDefaultDash.data)) {
      const initialFilter = getDashboardFilter(respDefaultDash.data);
      // Non-owners: use entities from the user config but dateRange from the
      // dashboard row so they inherit the owner's range on load.
      setFilter(
        applyStoredRange(initialFilter, respDefaultDash.data, resp.data)
      );
      const baseInput = getDashboardInput(respDefaultDash.data);
      setInput({
        ...baseInput,
        data: {
          ...baseInput.data,
          dateRange: resp.data?.data?.dateRange,
        },
      });
    } else {
      const initialFilter = getDashboardFilter(resp.data);
      setFilter(applyStoredRange(initialFilter, resp.data));
      setInput(getDashboardInput(resp.data));
    }
    setFetchedInProgress(false);
  };

  useEffect(() => {
    if (prefetch) {
      fetchData();
    }
  }, [pk, prefetch]);

  /**********/
  /* submit */
  /**********/

  const {enqueueSnackbar} = useSnackbar();
  const [submittedInProgress, setSubmittedInProgress] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState<number[]>([]);

  useEffect(() => {
    reduxActions.app.setApp(reduxDispatch, {
      inSubmittingInSavingDashboard: submittedInProgress,
    });
  }, [submittedInProgress]);

  useEffect(() => {
    reduxActions.app.setApp(reduxDispatch, {
      activedDashboard: fetchedData,
    });
  }, [fetchedData]);

  useEffect(() => {
    return () => {
      reduxActions.app.setApp(reduxDispatch, {
        activedDashboard: null,
        inSubmittingInSavingDashboard: false,
      });
    };
  }, []);

  const updateRoles = (resp: Dashboard) => {
    setSelectedRoles((selectedRoles) => [
      ...selectedRoles,
      ...(resp.visible_to_client_manager ? [Role.clientManager] : []),
      ...(resp.visible_to_client_viewer ? [Role.clientViewer] : []),
    ]);
  };

  const submitUserData = async (data: DashboardUpsertInputBody) => {
    setSubmittedInProgress(true);
    try {
      const userData = {
        data: data.data,
        dashboard_id: pk,
      };
      const endpointUserData = `${apiBaseUrl}/dashboard/configuration`;
      await API.post<DashboardDefault>(endpointUserData, userData);
    } catch (error: any) {
      const messages = getMessagesFromApiError(error, 'STRA_ed329466');
      enqueueSnackbar(<SnackbarMessages messages={messages} />, {
        variant: 'error',
        action: CloseSnackbarAction,
      });
    }
    setSubmittedInProgress(false);
  };

  const submitData = async (data: DashboardUpsertInputBody) => {
    setSubmittedInProgress(true);
    try {
      if (!checkPrivilegeAction(me, fetchedData, 'EDIT')) {
        const userData = {
          data: data.data,
          dashboard_id: pk,
        };
        const endpointUserData = `${apiBaseUrl}/dashboard/configuration`;
        await API.post<DashboardDefault>(endpointUserData, userData);
        savedEntitiesRef.current = data.data?.entities ?? [];
      } else {
        const endpoint = pk
          ? `${apiBaseUrl}/dashboard/${pk}`
          : `${apiBaseUrl}/dashboard`;
        const resp = pk
          ? await API.patch<Dashboard>(endpoint, data)
          : await API.post<Dashboard>(endpoint, data);
        const endpointShare = `${apiBaseUrl}/dashboard/${pk ? pk : item?.id}/share-to-user-type`;
        await API.patch<Dashboard>(endpointShare, {type: selectedRoles});
        const message = `Dashboard successfully ${pk ? 'updated' : 'created'}`;
        enqueueSnackbar(message, {
          variant: 'success',
          action: CloseSnackbarAction,
        });
        if (!_.isEqual(item, resp.data)) {
          setFetchedData(resp.data);
          setInput(getDashboardInput(resp.data));
        }
        savedEntitiesRef.current = resp.data?.data?.entities ?? [];
        onSubmitted?.(resp.data);
      }
    } catch (error: any) {
      const messages = getMessagesFromApiError(error, 'STRA_fa9dc493');
      enqueueSnackbar(<SnackbarMessages messages={messages} />, {
        variant: 'error',
        action: CloseSnackbarAction,
      });
    }
    setSubmittedInProgress(false);
  };

  const [updatedDashboardInProgress, setUpdatedDashboardInProgress] =
    useState(false);
  const updateDashboard = async (data: DashboardUpsertInputBody) => {
    // Save dashboard data in background to update panel filters
    setUpdatedDashboardInProgress(true);
    try {
      if (pk) {
        await API.patch<Dashboard>(`${apiBaseUrl}/dashboard/${pk}`, data);
      }
    } catch (error: any) {
      const messages = getMessagesFromApiError(error, 'STRA_16927a4b');
      enqueueSnackbar(<SnackbarMessages messages={messages} />, {
        variant: 'error',
        action: CloseSnackbarAction,
      });
    }
    setUpdatedDashboardInProgress(false);
  };

  /*********/
  /* input */
  /*********/
  const [input, setInput] = useState(() => getDashboardInput(fetchedData));
  const [filter, setFilter] = useState(() => getDashboardFilter(input));
  const savedEntitiesRef = useRef<DashboardEntity[]>(
    fetchedData?.data?.entities ?? []
  );

  const addDataEntity = (code: PanelCode) => {
    const id = Math.random().toString(16).substring(2, 14);

    let initialParams: Record<string, string> = {};
    if (panelUsesDashboardDate(code) && input.data?.dateRange) {
      const resolved = resolveDateRangePreset(
        input.data.dateRange.preset,
        input.data.dateRange.date_start,
        input.data.dateRange.date_end
      );
      initialParams = {
        date_start: resolved.date_start,
        date_end: resolved.date_end,
      };
    }

    setFilter({
      ...filter,
      [id]: {
        chartType: 'bar',
        params: initialParams,
      },
    });

    setInput({
      ...input,
      data: {
        ...input.data,
        entities: [
          ...(input.data?.entities ?? []),
          {
            id,
            type: 'panel',
            name: t(`panels.${code}`),
            panel: {
              code,
              data: {},
            },
            grid: {
              x: 0,
              y: (_.maxBy(input.data?.entities, 'grid.y')?.grid.y ?? 0) + 1,
              w: 12,
              h: 4,
            },
          },
        ],
      },
    });
    setTimeout(() => {
      const section = document.querySelector(`[data-id="${id}"]`);
      section?.scrollIntoView({behavior: 'smooth', block: 'start'});
    }, 100);

    return id;
  };

  const getDataEntityDefaults = (
    panelCode: PanelCode,
    viewType: DashboardHistoryReportType | 'assets' | 'employees',
    id: number | string
  ): DashboardEntity => {
    const isAMSReport = ['amsShortTerm', 'amsLongTerm', 'amsLocation'].includes(
      viewType as string
    );

    if (isAMSReport && panelCode === 'GasMonitoringReport') {
      return {
        id: Math.random().toString(16).substring(2, 14),
        type: 'panel',
        name: t(`panels.${panelCode}`),
        panel: {
          code: panelCode,
          data: {
            viewType,
            activeId: id,
            openedItems: [id],
            amsShortTerm: {
              tabs:
                viewType === 'amsShortTerm'
                  ? {
                      [id as string]: {
                        id: id as string,
                        selectAll: true,
                        chartMode: 'value',
                        columnWidths: {},
                      },
                    }
                  : {},
            },
            amsLongTerm: {
              tabs:
                viewType === 'amsLongTerm'
                  ? {
                      [id as string]: {
                        id: id as string,
                        selectAll: true,
                        columnWidths: {},
                      },
                    }
                  : {},
            },
            amsLocation: {
              mapLayers: ['mine', 'ams_location'],
              mapLevel: null,
              tabs:
                viewType === 'amsLocation'
                  ? {
                      [id as string]: {
                        id: id as string,
                        selectAll: true,
                        columnWidths: {},
                      },
                    }
                  : {},
            },
          },
        },
        grid: {
          x: 0,
          y: (_.maxBy(input.data?.entities, 'grid.y')?.grid.y ?? 0) + 1,
          w: 12,
          h: 4,
        },
      };
    }

    return {
      id: Math.random().toString(16).substring(2, 14),
      type: 'panel',
      name: t(`panels.${panelCode}`),
      panel: {
        code: panelCode,
        data: {
          viewType,
          [viewType]: {
            activeId: id,
            openedItems: [{id}],
          },
        },
      },
      grid: {
        x: 0,
        y: (_.maxBy(input.data?.entities, 'grid.y')?.grid.y ?? 0) + 1,
        w: 12,
        h: 4,
      },
    };
  };

  const handleDrillDown = (action: DashboardDrilldownAction) => {
    const {code} = action;
    const entity = input.data?.entities?.filter(
      (el) => el.panel?.code === code
    )[0];
    const exists = !!entity;

    if (exists) {
      setFilter({
        ...filter,
        [entity.id]: {
          ...filter[entity.id],
          chartType: action.chartType,
          params: {
            ...filter[entity.id].params,
            ...action.filter,
          },
        },
      });

      setTimeout(() => {
        const section = document.querySelector(`[data-id="${entity.id}"]`);
        section?.scrollIntoView({behavior: 'smooth', block: 'start'});
      }, 100);
    } else {
      // add
      const id = addDataEntity(code);

      setFilter({
        ...filter,
        [id]: {
          chartType: action.chartType,
          params: {
            ...action.filter,
          },
        },
      });
    }
  };

  /**
   * Called by DashboardDateRangeControl when the user clicks Apply.
   * Fans out the resolved range to panels and persists the chosen preset.
   */
  const handleDashboardDateApply = (
    range: DashboardDateRange,
    resolved: {date_start: string; date_end: string}
  ) => {
    const entities = input.data?.entities ?? [];
    const nextFilter = applyDashboardDateToFilter(
      resolved,
      entities,
      filter,
      range.applyToOverridden ?? false
    );
    setFilter(nextFilter);

    // When "Also update panels with custom ranges" is checked, re-attach all
    // previously detached participating panels so they resume following the
    // dashboard date going forward.
    const nextEntities = range.applyToOverridden
      ? entities.map((entity) => {
          if (
            entity.type === 'panel' &&
            entity.panel?.code &&
            panelUsesDashboardDate(entity.panel.code) &&
            entity.panel?.data?.detachedFromDashboardDate
          ) {
            return {
              ...entity,
              panel: {
                ...entity.panel,
                data: {
                  ...entity.panel.data,
                  detachedFromDashboardDate: false,
                },
              },
            };
          }
          return entity;
        })
      : entities;

    const nextInput: DashboardUpsertInputBody = {
      ...input,
      data: {
        ...input.data,
        dateRange: range,
        entities: nextEntities,
      },
    };
    setInput(nextInput);

    // Persist immediately so the range survives a page reload.
    // Owners: the range lives on the dashboard row (PATCH); persist now so
    // non-owners also inherit it without a manual Save click.
    // Non-owners: save to the per-user config blob.
    if (checkPrivilegeAction(me, fetchedData, 'EDIT')) {
      updateDashboard({...nextInput, name: fetchedData?.name || null});
    } else {
      submitUserData(nextInput);
    }
  };

  const handleOpenEntityHistory = (
    id: number | string | number[],
    type: DashboardHistoryReportType
  ) => {
    setTimeout(() => {
      let panelCode: PanelCode = 'CommtracNodeTrackingReports';

      if (['cn', 'commtracNodeByCn', 'wifi', 'wifiLongTerm'].includes(type)) {
        panelCode = 'NodeTrackingReports';
      } else if (['networkDiagnostics'].includes(type)) {
        panelCode = 'NetworkDiagnostics';
      } else if (['alarm'].includes(type)) {
        panelCode = 'AlarmHistoryReports';
      } else if (['alarm_log'].includes(type)) {
        panelCode = 'AlarmLogReports';
      } else if (['hazard_ai_detection_log'].includes(type)) {
        panelCode = 'HazardAIDectionLog';
      } else if (['hazard_ai_heatmap'].includes(type)) {
        panelCode = 'HazardHeatMapReports';
      } else if (['hazard_ai_machine_location'].includes(type)) {
        panelCode = 'HazardAIMachineLocationHistory';
      } else if (
        ['amsShortTerm', 'amsLongTerm', 'amsLocation'].includes(type)
      ) {
        panelCode = 'GasMonitoringReport';
      } else if (
        ['amsEmoduleSensorHistory', 'amsEmoduleInstallationHistory'].includes(
          type
        )
      ) {
        panelCode = _.upperFirst(type) as PanelCode;
      } else if (['amsEmoduleCalibration'].includes(type)) {
        panelCode = 'AMSEModuleCalibration';
      } else if (['beltHistoryReport'].includes(type)) {
        panelCode = _.upperFirst(type) as PanelCode;
      } else if (['beltLocationHistoryReport'].includes(type)) {
        panelCode = _.upperFirst(type) as PanelCode;
      } else if (['machine_detections_by_camera'].includes(type)) {
        panelCode = 'MachineDetectionsByCamera';
      }

      const reportedElement = document.querySelector(
        `[data-panel-code="${panelCode}"]`
      );
      if (reportedElement) {
        reportedElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest',
        });
      }
    }, 100);
    if (['asset', 'employee'].includes(type)) {
      const viewType = type === 'asset' ? 'assets' : 'employees';
      const panelCode: PanelCode = 'CommtracNodeTrackingReports';
      const panelIndex =
        input.data?.entities?.findIndex((i) => i.panel?.code === panelCode) ??
        -1;
      if (panelIndex === -1) {
        const panel = getDataEntityDefaults(panelCode, viewType, id as number);
        setInput(
          update(input, {
            data: {
              entities: {
                $set: [...(input.data?.entities ?? []), panel],
              },
            },
          })
        );
      } else if (
        input.data?.entities?.[panelIndex].panel?.data?.[
          viewType
        ]?.openedItems?.find((i: any) => i?.id === id)
      ) {
        setInput(
          update(input, {
            data: {
              entities: {
                [panelIndex]: {
                  panel: {
                    data: {
                      viewType: {
                        $set: viewType,
                      },
                      [viewType]: {
                        activeId: {
                          $set: id,
                        },
                      },
                    },
                  },
                },
              },
            },
          })
        );
      } else {
        setInput(
          update(input, {
            data: {
              entities: {
                [panelIndex]: {
                  panel: {
                    data: {
                      viewType: {
                        $set: viewType,
                      },
                      [viewType]: {
                        $set: {
                          activeId: id,
                          openedItems: [
                            ...(input.data?.entities?.[panelIndex].panel
                              ?.data?.[viewType]?.openedItems ?? []),
                            {id},
                          ],
                        },
                      },
                    },
                  },
                },
              },
            },
          })
        );
      }
    } else if (
      ['cn', 'commtracNodeByCn', 'wifi', 'wifiLongTerm'].includes(type)
    ) {
      const viewType = type;
      const panelCode: PanelCode = 'NodeTrackingReports';
      const panelIndex =
        input?.data?.entities?.findIndex((i) => i.panel?.code === panelCode) ??
        -1;
      if (panelIndex === -1) {
        const panel = getDataEntityDefaults(panelCode, type, id as number);
        setInput(
          update(input, {
            data: {
              entities: {
                $set: [...(input.data?.entities ?? []), panel],
              },
            },
          })
        );
      } else if (
        input.data?.entities?.[panelIndex].panel?.data?.[
          viewType
        ]?.openedItems?.find((i: any) => i?.id === id)
      ) {
        setInput(
          update(input, {
            data: {
              entities: {
                [panelIndex]: {
                  panel: {
                    data: {
                      viewType: {
                        $set: viewType,
                      },
                      [viewType]: {
                        activeId: {
                          $set: id,
                        },
                      },
                    },
                  },
                },
              },
            },
          })
        );
      } else {
        setInput(
          update(input, {
            data: {
              entities: {
                [panelIndex]: {
                  panel: {
                    data: {
                      viewType: {
                        $set: viewType,
                      },
                      [viewType]: {
                        $set: {
                          activeId: id,
                          openedItems: [
                            ...(input.data?.entities?.[panelIndex].panel
                              ?.data?.[viewType]?.openedItems ?? []),
                            {id},
                          ],
                        },
                      },
                    },
                  },
                },
              },
            },
          })
        );
      }
    } else if (['networkDiagnostics'].includes(type)) {
      const viewType = 'general';
      const panelCode: PanelCode = 'NetworkDiagnostics';
      const panelIndex =
        input?.data?.entities?.findIndex((i) => i.panel?.code === panelCode) ??
        -1;
      if (panelIndex === -1) {
        addDataEntity(panelCode);
      } else if (input.data?.entities?.[panelIndex].panel?.data?.[viewType]) {
        setInput(
          update(input, {
            data: {
              entities: {
                [panelIndex]: {
                  panel: {
                    data: {
                      viewType: {
                        $set: viewType,
                      },
                    },
                  },
                },
              },
            },
          })
        );
      } else {
        setInput(
          update(input, {
            data: {
              entities: {
                [panelIndex]: {
                  panel: {
                    data: {
                      viewType: {
                        $set: viewType,
                      },
                      [viewType]: {
                        $set: {
                          ...(input.data?.entities?.[panelIndex].panel?.data?.[
                            viewType
                          ] ?? {}),
                        },
                      },
                    },
                  },
                },
              },
            },
          })
        );
      }
    } else if (['alarm'].includes(type)) {
      const viewType = type;
      const panelCode: PanelCode = 'AlarmHistoryReports';
      const panelIndex =
        input.data?.entities?.findIndex((i) => i.panel?.code === panelCode) ??
        -1;

      if (panelIndex === -1) {
        const panel = getDataEntityDefaults(panelCode, type, id as number);
        setInput(
          update(input, {
            data: {
              entities: {
                $set: [...(input.data?.entities ?? []), panel],
              },
            },
          })
        );
      } else if (
        input.data?.entities?.[panelIndex].panel?.data?.[
          viewType
        ]?.openedItems?.find((i: any) => i?.id === id)
      ) {
        setInput(
          update(input, {
            data: {
              entities: {
                [panelIndex]: {
                  panel: {
                    data: {
                      viewType: {
                        $set: viewType,
                      },
                      [viewType]: {
                        activeId: {
                          $set: id,
                        },
                      },
                    },
                  },
                },
              },
            },
          })
        );
      } else {
        setInput(
          update(input, {
            data: {
              entities: {
                [panelIndex]: {
                  panel: {
                    data: {
                      viewType: {
                        $set: viewType,
                      },
                      [viewType]: {
                        $set: {
                          activeId: id,
                          openedItems: [
                            ...(input.data?.entities?.[panelIndex].panel
                              ?.data?.[viewType]?.openedItems ?? []),
                            {id},
                          ],
                        },
                      },
                    },
                  },
                },
              },
            },
          })
        );
      }
    } else if (['alarm_log'].includes(type)) {
      const viewType = type;
      const panelCode: PanelCode = 'AlarmLogReports';
      const panelIndex =
        input.data?.entities?.findIndex((i) => i.panel?.code === panelCode) ??
        -1;
      if (panelIndex === -1) {
        const panel = getDataEntityDefaults(panelCode, type, id as number);
        setInput(
          update(input, {
            data: {
              entities: {
                $set: [...(input.data?.entities ?? []), panel],
              },
            },
          })
        );
      } else if (
        input.data?.entities?.[panelIndex].panel?.data?.[
          viewType
        ]?.openedItems?.find((i: any) => i?.id === id)
      ) {
        setInput(
          update(input, {
            data: {
              entities: {
                [panelIndex]: {
                  panel: {
                    data: {
                      viewType: {
                        $set: viewType,
                      },
                      [viewType]: {
                        activeId: {
                          $set: id,
                        },
                      },
                    },
                  },
                },
              },
            },
          })
        );
      } else {
        setInput(
          update(input, {
            data: {
              entities: {
                [panelIndex]: {
                  panel: {
                    data: {
                      viewType: {
                        $set: viewType,
                      },
                      [viewType]: {
                        $set: {
                          activeId: id,
                          openedItems: [
                            ...(input.data?.entities?.[panelIndex].panel
                              ?.data?.[viewType]?.openedItems ?? []),
                            {id},
                          ],
                        },
                      },
                    },
                  },
                },
              },
            },
          })
        );
      }
    } else if (['hazard_ai_detection_log'].includes(type)) {
      const viewType = type;
      const panelCode: PanelCode = 'HazardAIDectionLog';
      const panelIndex =
        input.data?.entities?.findIndex((i) => i.panel?.code === panelCode) ??
        -1;
      const panel = getDataEntityDefaults(panelCode, type, id as number);
      if (panelIndex === -1) {
        setInput(
          update(input, {
            data: {
              entities: {
                $set: [...(input.data?.entities ?? []), panel],
              },
            },
          })
        );
      } else if (
        input.data?.entities?.[panelIndex].panel?.data?.[
          viewType
        ]?.openedItems?.find((i: any) => i?.id === id)
      ) {
        setInput(
          update(input, {
            data: {
              entities: {
                [panelIndex]: {
                  panel: {
                    data: {
                      viewType: {
                        $set: viewType,
                      },
                      [viewType]: {
                        activeId: {
                          $set: id,
                        },
                      },
                    },
                  },
                },
              },
            },
          })
        );
      } else {
        setInput(
          update(input, {
            data: {
              entities: {
                [panelIndex]: {
                  panel: {
                    data: {
                      viewType: {
                        $set: viewType,
                      },
                      [viewType]: {
                        $set: {
                          activeId: id,
                          openedItems: [
                            ...(input.data?.entities?.[panelIndex].panel
                              ?.data?.[viewType]?.openedItems ?? []),
                            {id},
                          ],
                        },
                      },
                    },
                  },
                },
              },
            },
          })
        );
      }
    } else if (['hazard_ai_heatmap'].includes(type)) {
      const viewType = type;
      const panelCode: PanelCode = 'HazardHeatMapReports';
      const panelIndex =
        input.data?.entities?.findIndex((i) => i.panel?.code === panelCode) ??
        -1;
      const uniqId = (_.isArray(id) ? [...id] : [id])?.join('-');
      if (panelIndex === -1) {
        const panel = getDataEntityDefaults(panelCode, type, uniqId as string);
        setInput(
          update(input, {
            data: {
              entities: {
                $set: [...(input.data?.entities ?? []), panel],
              },
            },
          })
        );
      } else if (
        input.data?.entities?.[panelIndex].panel?.data?.[
          viewType
        ]?.openedItems?.find((i: any) => i?.id === uniqId)
      ) {
        // Tab already open for this machine — just activate it
        setInput(
          update(input, {
            data: {
              entities: {
                [panelIndex]: {
                  panel: {
                    data: {
                      viewType: {$set: viewType},
                      [viewType]: {activeId: {$set: uniqId}},
                    },
                  },
                },
              },
            },
          })
        );
      } else {
        setInput(
          update(input, {
            data: {
              entities: {
                [panelIndex]: {
                  panel: {
                    data: {
                      viewType: {
                        $set: viewType,
                      },
                      [viewType]: {
                        $set: {
                          activeId: uniqId,
                          openedItems: [
                            ...(input.data?.entities?.[panelIndex].panel
                              ?.data?.[viewType]?.openedItems ?? []),
                            {id: uniqId},
                          ],
                        },
                      },
                    },
                  },
                },
              },
            },
          })
        );
      }
    } else if (['hazard_ai_machine_location'].includes(type)) {
      const panels = input.data?.entities || [];
      const panelCode: PanelCode = 'HazardAIMachineLocationHistory';
      const panelIndex =
        input.data?.entities?.findIndex((i) => i.panel?.code === panelCode) ??
        -1;

      if (panelIndex === -1) {
        // Add panel if it doesn't exist
        const panel = {
          id: Math.random().toString(16).substring(2, 14),
          type: 'panel',
          name: t(`panels.${panelCode}`),
          panel: {
            code: panelCode,
            data: {
              viewType: type,
              [type]: {
                selectedMachineId: id,
              },
            },
          },
          grid: {
            x: 0,
            y: (_.maxBy(input.data?.entities, 'grid.y')?.grid.y ?? 0) + 1,
            w: 12,
            h: 4,
          },
        } as DashboardEntity;

        setInput(
          update(input, {
            data: {
              entities: {
                $set: [...panels, panel],
              },
            },
          })
        );
      } else {
        const spec = {
          data: {
            entities: {
              [panelIndex]: {
                panel: {
                  data: {
                    [type]: {
                      $set: {
                        selectedMachineId: id,
                      },
                    },
                  },
                },
              },
            },
          },
        } as Spec<DashboardUpsertInputBody, never>;

        setInput(update(input, spec));
      }
    } else if (['amsShortTerm', 'amsLongTerm', 'amsLocation'].includes(type)) {
      const viewType = type;
      const panelCode: PanelCode = 'GasMonitoringReport';
      const panelIndex =
        input.data?.entities?.findIndex((i) => i.panel?.code === panelCode) ??
        -1;

      const uniqId = (_.isArray(id) ? [...id] : [id])?.join('-');
      if (panelIndex === -1) {
        const panel = getDataEntityDefaults(panelCode, type, uniqId as string);
        setInput(
          update(input, {
            data: {
              entities: {
                $set: [...(input.data?.entities ?? []), panel],
              },
            },
          })
        );
      } else if (
        input.data?.entities?.[panelIndex].panel?.data?.openedItems?.includes(
          uniqId
        )
      ) {
        setInput(
          update(input, {
            data: {
              entities: {
                [panelIndex]: {
                  panel: {
                    data: {
                      viewType: {
                        $set: viewType,
                      },
                      activeId: {
                        $set: uniqId,
                      },
                    },
                  },
                },
              },
            },
          })
        );
      } else {
        const existingTabs =
          input.data?.entities?.[panelIndex].panel?.data?.[viewType]?.tabs ??
          {};
        setInput(
          update(input, {
            data: {
              entities: {
                [panelIndex]: {
                  panel: {
                    data: {
                      viewType: {
                        $set: viewType,
                      },
                      activeId: {
                        $set: uniqId,
                      },
                      openedItems: {
                        $set: [
                          ...(input.data?.entities?.[panelIndex].panel?.data
                            ?.openedItems ?? []),
                          uniqId,
                        ],
                      },
                      [viewType]: {
                        tabs: {
                          $set: {
                            ...existingTabs,
                            [uniqId]: {
                              id: uniqId,
                              selectAll: true,
                              columnWidths: {},
                            },
                          },
                        },
                      },
                    },
                  },
                },
              },
            },
          })
        );
      }
    } else if (['amsEmoduleCalibration'].includes(type)) {
      const viewType = type;
      const panelCode: PanelCode = 'AMSEModuleCalibration';
      const panelIndex =
        input.data?.entities?.findIndex((i) => i.panel?.code === panelCode) ??
        -1;

      const uniqId = (_.isArray(id) ? [...id] : [id])?.join('-');
      if (panelIndex === -1) {
        const panel = getDataEntityDefaults(panelCode, type, uniqId as string);
        setInput(
          update(input, {
            data: {
              entities: {
                $set: [...(input.data?.entities ?? []), panel],
              },
            },
          })
        );
      } else if (
        input.data?.entities?.[panelIndex].panel?.data?.[
          viewType
        ]?.openedItems?.find((i: any) => i?.id === uniqId)
      ) {
        setInput(
          update(input, {
            data: {
              entities: {
                [panelIndex]: {
                  panel: {
                    data: {
                      viewType: {
                        $set: viewType,
                      },
                      [viewType]: {
                        activeId: {
                          $set: uniqId,
                        },
                      },
                    },
                  },
                },
              },
            },
          })
        );
      } else {
        setInput(
          update(input, {
            data: {
              entities: {
                [panelIndex]: {
                  panel: {
                    data: {
                      viewType: {
                        $set: viewType,
                      },
                      [viewType]: {
                        $set: {
                          activeId: uniqId,
                          openedItems: [
                            ...(input.data?.entities?.[panelIndex].panel
                              ?.data?.[viewType]?.openedItems ?? []),
                            {id: uniqId},
                          ],
                        },
                      },
                    },
                  },
                },
              },
            },
          })
        );
      }
    } else if (
      ['amsEmoduleSensorHistory', 'amsEmoduleInstallationHistory'].includes(
        type
      )
    ) {
      const panels = input.data?.entities || [];
      const panelCode = _.upperFirst(type) as PanelCode;
      const panelIndex = panels?.findIndex((i) => i.panel?.code === panelCode);

      if (panelIndex === -1) {
        // Add panel if it doesn't exist
        const panel = getDataEntityDefaults(panelCode, type, id as number);
        setInput(
          update(input, {
            data: {
              entities: {
                $set: [...panels, panel],
              },
            },
          })
        );
      } else {
        // Check if tab/item already added and add new tab
        // if it doesn't exist or activate existing tab
        const openedItems =
          panels[panelIndex].panel?.data?.[type]?.openedItems || [];
        const openedItem = openedItems.find((i: any) => i?.id === id);
        const panel = update(panels[panelIndex], {
          panel: {
            data: {
              viewType: {
                $set: type,
              },
              [type]: {
                $set: {
                  activeId: id,
                  openedItems: !openedItem
                    ? [...openedItems, {id}]
                    : openedItems,
                },
              },
            },
          },
        });
        setInput(
          update(input, {
            data: {
              entities: {
                [panelIndex]: {
                  $set: panel,
                },
              },
            },
          })
        );
      }
      // } else if (['amsEmoduleInstallationHistory'].includes(type)) {
      //   const panels = input.data?.entities || [];
      //   const panelCode = _.upperFirst(type) as PanelCode;
      //   const panelIndex = panels?.findIndex((i) => i.panel?.code === panelCode);

      //   if (panelIndex === -1) {
      //     const panel = getDataEntityDefaults(panelCode, type, id as number);
      //     setInput(
      //       update(input, {
      //         data: {
      //           entities: {
      //             $set: [...panels, panel],
      //           },
      //         },
      //       })
      //     );
      //   } else {
      //     const openedItems =
      //       panels[panelIndex].panel?.data?.[type]?.openedItems || [];
      //     const openedItem = openedItems.find((i: any) => i?.id === id);
      //     const panel = update(panels[panelIndex], {
      //       panel: {
      //         data: {
      //           viewType: {
      //             $set: type,
      //           },
      //           [type]: {
      //             $set: {
      //               activeId: id,
      //               openedItems: !openedItem
      //                 ? [...openedItems, {id}]
      //                 : openedItems,
      //             },
      //           },
      //         },
      //       },
      //     });
      //     setInput(
      //       update(input, {
      //         data: {
      //           entities: {
      //             [panelIndex]: {
      //               $set: panel,
      //             },
      //           },
      //         },
      //       })
      //     );
      //   }
    } else if (['beltHistoryReport'].includes(type)) {
      const panels = input.data?.entities || [];
      const panelCode = _.upperFirst(type) as PanelCode;
      const panelIndex = panels?.findIndex((i) => i.panel?.code === panelCode);

      if (panelIndex === -1) {
        // Add panel if it doesn't exist
        const panel = getDataEntityDefaults(panelCode, type, id as number);
        setInput(
          update(input, {
            data: {
              entities: {
                $set: [...panels, panel],
              },
            },
          })
        );
      } else {
        // Check if tab/item already added and add new tab
        // if it doesn't exist or activate existing tab
        const openedItems =
          panels[panelIndex].panel?.data?.[type]?.openedItems || [];
        const openedItem = openedItems.find((i: any) => i?.id === id);
        const panel = update(panels[panelIndex], {
          panel: {
            data: {
              viewType: {
                $set: type,
              },
              [type]: {
                $set: {
                  activeId: id,
                  openedItems: !openedItem
                    ? [...openedItems, {id}]
                    : openedItems,
                },
              },
            },
          },
        });
        setInput(
          update(input, {
            data: {
              entities: {
                [panelIndex]: {
                  $set: panel,
                },
              },
            },
          })
        );
      }
    } else if (['beltLocationHistoryReport'].includes(type)) {
      const panels = input.data?.entities || [];
      const panelCode = _.upperFirst(type) as PanelCode;
      const panelIndex = panels?.findIndex((i) => i.panel?.code === panelCode);

      if (panelIndex === -1) {
        // Add panel if it doesn't exist
        const panel = getDataEntityDefaults(panelCode, type, id as number);
        setInput(
          update(input, {
            data: {
              entities: {
                $set: [...panels, panel],
              },
            },
          })
        );
      } else {
        // Check if tab/item already added and add new tab
        // if it doesn't exist or activate existing tab
        const openedItems =
          panels[panelIndex].panel?.data?.[type]?.openedItems || [];
        const openedItem = openedItems.find((i: any) => i?.id === id);
        const panel = update(panels[panelIndex], {
          panel: {
            data: {
              viewType: {
                $set: type,
              },
              [type]: {
                $set: {
                  activeId: id,
                  openedItems: !openedItem
                    ? [...openedItems, {id}]
                    : openedItems,
                },
              },
            },
          },
        });
        setInput(
          update(input, {
            data: {
              entities: {
                [panelIndex]: {
                  $set: panel,
                },
              },
            },
          })
        );
      }
    } else if (['machine_detections_by_camera'].includes(type)) {
      const code: PanelCode = 'MachineDetectionsByCamera';
      const entity = input.data?.entities?.find(
        (el) => el.panel?.code === code
      );
      if (entity) {
        setFilter({
          ...filter,
          [entity.id]: {
            ...filter[entity.id],
            params: {...filter[entity.id]?.params, machine_id: id as number},
          },
        });
      } else {
        const newId = addDataEntity(code);
        setFilter({
          ...filter,
          [newId]: {params: {machine_id: id as number}},
        });
      }
    }
  };

  const handleEntityUpdate = (entity: DashboardEntity) => {
    const entities = savedEntitiesRef.current.map((e) =>
      e.id === entity.id
        ? {...e, panel: {...e.panel, data: entity.panel?.data}}
        : e
    );
    // Preserve dateRange so every panel-config save doesn't wipe the stored range.
    if (checkPrivilegeAction(me, fetchedData, 'EDIT') && !hasVisibleItems) {
      updateDashboard({
        ...input,
        data: {entities, dateRange: input.data?.dateRange},
        name: fetchedData?.name || null,
      });
    } else {
      submitUserData({
        ...input,
        data: {entities, dateRange: input.data?.dateRange},
      });
    }
  };

  return (
    <DashboardProvider
      value={input}
      onChange={setInput}
      filter={{filter, setFilter}}
      drilldown={handleDrillDown}
    >
      <Box
        position="relative"
        height="100%"
        sx={{
          display: input.data?.entities?.length === 1 ? 'flex' : 'block',
          flexDirection: 'column',
        }}
        id="entity-container"
      >
        <DashboardDebugPanel
          savedEntities={savedEntitiesRef.current}
          currentEntities={input.data?.entities ?? []}
        />
        <Backdrop
          open={fetchedInProgress}
          sx={{position: 'absolute', zIndex: 1001}}
        >
          <CircularProgress color="inherit" />
        </Backdrop>

        {fetchedErrors.map((error, index) => (
          <Alert key={index} sx={{mb: 2}} severity="error">
            {error}
          </Alert>
        ))}

        {fetchedData && (
          <>
            {/* Dashboard-wide date range — portalled into the top bar via the
                DashboardHeaderActionsSlot. Visible whenever ≥1 panel participates. */}
            {dashboardHasDatePanels(input.data?.entities ?? []) && (
              <DashboardHeaderActionsSlot>
                <DashboardDateRangeControl
                  value={input.data?.dateRange}
                  onApply={handleDashboardDateApply}
                  editable={checkPrivilegeAction(me, fetchedData, 'EDIT')}
                />
              </DashboardHeaderActionsSlot>
            )}

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                mb: 2,
                position: 'absolute',
                width: '0px',
                overflow: 'hidden',
              }}
            >
              <Typography component="h1" variant="h5">
                {fetchedData.name}
              </Typography>

              <Box display="flex" alignItems="center" gap={1}>
                <DashboardPanelSelectButton
                  component={Button}
                  componentProps={{
                    sx: {minWidth: 'auto'},
                    id: 'btn-add-new-item',
                  }}
                  onUpdate={addDataEntity}
                >
                  <AddIcon />
                </DashboardPanelSelectButton>

                <AccessControl
                  accessChecker={() =>
                    checkPrivilegeAction(me, fetchedData, 'EDIT')
                  }
                >
                  <Button
                    sx={{minWidth: 'auto'}}
                    id="btn-share-to-role"
                    onClick={() => {
                      setAction(!action);
                    }}
                  >
                    <ShareIcon />
                  </Button>
                </AccessControl>

                <AccessControl
                  permissions={['patch::/dashboard/:id(\\d+)']}
                  accessChecker={() =>
                    checkPrivilegeAction(me, fetchedData, 'EDIT')
                  }
                >
                  <LoadingButton
                    sx={{minWidth: 'auto'}}
                    loading={submittedInProgress || updatedDashboardInProgress}
                    id="btn-save-dashboard"
                    onClick={() => submitData(input)}
                  >
                    <SaveIcon />
                  </LoadingButton>
                </AccessControl>
              </Box>
            </Box>

            <DashboardEntityList
              entities={input.data?.entities ?? []}
              onUpdate={(v) =>
                setInput(
                  update(input, {
                    data: {
                      entities: {
                        $set: v,
                      },
                    },
                  })
                )
              }
              onEntityUpdate={handleEntityUpdate}
              onOpenHistory={handleOpenEntityHistory}
            />
          </>
        )}
      </Box>
      <ModalFixed open={action} onClose={() => setAction(false)}>
        <DashboardItemShareModal
          item={fetchedData}
          isEditing={true}
          onSelectRole={(roles: number[]) => setSelectedRoles(roles)}
          onClose={() => setAction(false)}
        />
      </ModalFixed>
    </DashboardProvider>
  );
};

export default DashboardItem;
